<template>
    <div class="day-grid-month" ref="container">
        <div class="days-labels">
            <div class="day-label" v-for="day in dayLabels" :key="day">
                {{ day }}
            </div>
        </div>
        <div class="days-cells">
            <div 
                v-for="(day, cellIndex) in daysInGrid" :key="cellIndex"
                class="day-cell" 
                ref="cellRefs"
                :class="{
                    'past': day.isPast,
                    'current-month': day.isCurrentMonth,
                    'not-current-month': !day.isCurrentMonth,
                    'today': day.isToday,
                    'has-events': getEventsForCell(day.dateKey).length > 0 || (eventsByDate[day.dateKey] && eventsByDate[day.dateKey].length > maxVisibleEvents)
                }"
                :data-drag-event-location="day.dateString"
                @click="(e) => selectDate(e, day.dateKey)"
            >
                <div class="header-cell">
                    <div class="date-number">{{ day.dayNumber }}</div>
                </div>
                <div class="events">
                    <Event 
                        v-for="event in getEventsForCell(day.dateKey)" 
                        :key="event.id"
                        v-bind="event"
                        :isPlaceholder="eventDragged.eventId === event.id"
                        :isDraggable="event.editable !== false"
                        @eventDragStart="eventDragStart"
                        @eventClick="$emit('eventClick', event.id)"
                      
                    />
                    <div 
                        v-if="eventsByDate[day.dateKey] && eventsByDate[day.dateKey].length > maxVisibleEvents" 
                        class="btn-more"
                        @click.stop="showMoreEvents(day.dateKey, cellIndex)"
                    >
                        {{ $t('dash.posts.planner.more-events', { count: eventsByDate[day.dateKey].length - maxVisibleEventsWithBtn }) }}
                    </div>
                </div>
            </div>
        </div>

        <EventsPopUp
            v-if="popUpOptions.cellKey"
            @close="closeMoreEvents"
            @eventDragStart="eventDragStart"
            @eventClick="(id) => $emit('eventClick', id)"
            :eventDragged="eventDragged"
            :events="eventsByDate[popUpOptions.cellKey]"
            :containerRef="$refs.container"
            :anchorRef="popUpOptions.anchorRef"
            :title="popUpOptions.dateLabel"

        />
        <DraggableEvent 
            v-bind="eventDragged"
            @eventDragStop="eventDragStop"
        />
    </div>

</template>

<script>

import Event from '../Event.vue'
import EventsPopUp from '../EventsPopUp.vue'
import DraggableEvent from '../DraggableEvent.vue';

export default {
    components: {
        Event,
        EventsPopUp,
        DraggableEvent
    },

    props: {
        currentDate: {
            type: String,
            default: ()=> new Date().toISOString()
        },
        events: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            maxVisibleEvents: 3, // Valeur par défaut
            maxVisibleEventsWithBtn: 2,
            resizeFirtCellObserver: null,
            popUpOptions: {
                cellKey: null,
                anchorRef: null,
                dateLabel: null,
                position: { top: 0, left: 0}
            },

            eventDragged: {
                e:null,
                el:null,
                eventId: null,
                event: null
            },
        }
    },

    mounted () {
        this.resizeFirtCellObserver = new ResizeObserver(entries => {
            this.calculMaxVisibleEvents()
        });

        if (this.$refs?.cellRefs[0]) {
            this.resizeFirtCellObserver.observe(this.$refs.cellRefs[0]);
        }
        this.calculMaxVisibleEvents()
    },

    methods: {
        calculMaxVisibleEvents() {    
            const firstCell = this.$refs?.cellRefs[0]; 
            const eventsEl = firstCell ? firstCell.querySelector('.events') : null;
            if (eventsEl) {
                const cellHeight = eventsEl.clientHeight;
                const calculatedLimit = Math.floor(cellHeight / 41);
                this.maxVisibleEvents = Math.max(1, calculatedLimit);
                this.maxVisibleEventsWithBtn = Math.floor((cellHeight-25) / 41)
            }
        },

        getEventsForCell(dateKey) {
            //prend en compte le button "+ X autres"
            if (this.eventsByDate[dateKey]) {
                if (this.eventsByDate[dateKey].length <= this.maxVisibleEvents) {
                    return this.eventsByDate[dateKey];
                }else{
                    //laisse de la place pour le bouton "+ X autres"
                    console.log('getEventsForCell limited', dateKey, this.eventsByDate[dateKey],  this.maxVisibleEvents)
                    return this.eventsByDate[dateKey].slice(0, this.maxVisibleEventsWithBtn);
                }
            }
            return [];
        },

        showMoreEvents(dateString, cellIndex) {
            if (!this.$refs.cellRefs[cellIndex]) return

            const date = new Date(dateString);

            const dateLabel = new Intl.DateTimeFormat(this.$i18n.locale || 'fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }).format(date);
            
            this.popUpOptions.anchorRef = this.$refs.cellRefs[cellIndex];
            this.popUpOptions.cellKey = dateString;
            this.popUpOptions.dateLabel = dateLabel;
        },

        closeMoreEvents() {
            this.popUpOptions.cellKey = null;
            this.popUpOptions.anchorRef = null;
            this.popUpOptions.dateLabel = null;
        },

        eventDragStop({ location_id }) {
       
            const event = this.eventDragged.event
            console.log('eventDragStop location_id', location_id);
            if (location_id) {
                const currentDate = new Date(event.date);
                const newDate = new Date(location_id);
                newDate.setHours(currentDate.getHours());
                newDate.setMinutes(currentDate.getMinutes());

                event.dateInstance = newDate;
                
                event.date = newDate.toISOString();
            }

            const index = this.events.findIndex(ev => ev.id ===  event.id);

            if (index !== -1) {
                this.$set(this.events, index, event);
            }else{
                console.log('Adding event', event);
                this.$set(this.events, this.events.length, {...event});
            }

            this.$emit('eventDragStop', { 
                event: {...event}
            });

            this.eventDragged = {
                e:null,
                eventId: null,
                event: null
            };
        },

        eventDragStart({ e, eventId, el }) {
            this.$emit('eventDragStart', { eventId });
            const event = this.events.find(ev => ev.id === eventId);
            this.eventDragged = {
                e,
                eventId,
                el,
                event: {...event}
            };
        },

        selectDate(e, dateKey) {

            if (e.target.closest('.container-event')) {
                return;
            }
            this.$emit('selectDate', dateKey);
        },

        //api
        prev() {
            const now = new Date(this.currentDate);
            this.$emit('gotoDate', new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString());
        },

        next() {    
            const now = new Date(this.currentDate);
            this.$emit('gotoDate', new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString());
        },

        getCurrentStart() {
            const date = new Date(this.currentDate);
            return new Date(date.getFullYear(), date.getMonth(), 1);
        },

        getCurrentEnd() {
            const date = new Date(this.currentDate);
            return new Date(date.getFullYear(), date.getMonth() + 1, 1);
        }
    },
    

    computed: {
        dayLabels() {
            const locale = this.$i18n.locale || 'fr-FR';
            const monday = new Date(2024, 0, 1); // 1er janvier 2024 = lundi
            return Array.from({ length: 7 }, (_, i) => {
                const d = new Date(monday);
                d.setDate(monday.getDate() + i);
                return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
            });
        },
        daysInGrid() {
            const date = new Date(this.currentDate);
            const year = date.getFullYear();
            const month = date.getMonth();

            const now = new Date();
            const todayStamp = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const todayString = now.toDateString();
            
            // 1. Premier jour du mois
            const firstDayOfMonth = new Date(year, month, 1);

            // 2. Jour de la semaine du premier jour (0=Dimanche, 1=Lundi...)
            // On ajuste si on veut que la semaine commence par Lundi
            let dayOfWeek = firstDayOfMonth.getDay(); 
            let blankDaysBefore = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Si Lundi est index 0

            // 3. Créer le point de départ de la grille (le premier jour affiché)
            const startDate = new Date(firstDayOfMonth);
            startDate.setDate(firstDayOfMonth.getDate() - blankDaysBefore);

            const calendarDays = [];
            const tempDate = new Date(startDate);

            // 4. Générer exactement 42 jours
            for (let i = 0; i < 42; i++) {
                const cellDateStamp = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate()).getTime();
                const dateString = tempDate.toDateString();

                calendarDays.push({
                    date: new Date(tempDate),
                    dayNumber: tempDate.getDate(),
                    isPast: cellDateStamp < todayStamp,
                    isCurrentMonth: tempDate.getMonth() === month,
                    isToday: dateString === todayString,
                    dateString: dateString,
                    dateKey: tempDate.toLocaleDateString('sv'),
                    timestamp: tempDate.getTime()
                });
                tempDate.setDate(tempDate.getDate() + 1);
            }

            return calendarDays;
        },

        eventsByDate() {
            console.log('eventsByDate recalculated')

            const eventsObj = this.events.reduce((acc, event) => {
                event.dateInstance = new Date(event.date);
                // const dateKey = event.dateInstance.toDateString();
                const dateKey = event.dateInstance.toLocaleDateString('sv')

                if (!acc[dateKey]) acc[dateKey] = [];
                acc[dateKey].push(event);
                return acc
            }, {})
        
            for (const [dateKey, events] of Object.entries(eventsObj)) {
                eventsObj[dateKey] = events.sort((a, b) => a.dateInstance - b.dateInstance);
            }

            return eventsObj;
        }
    },

    watch: {
        currentDate() {
            this.closeMoreEvents()
        },
        events() {
            // this.closeMoreEvents()
        }
    },

    beforeDestroy() {
        if (this.resizeFirtCellObserver) {
            this.resizeFirtCellObserver.disconnect();
        }
    }
}
</script>